import {
  CalendarFilled,
  DeleteFilled,
  DollarCircleFilled,
} from "@ant-design/icons";
import { Button, Table } from "antd";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDeleteTripExpense } from "../../api/api.delete";
import { useGetTripById, useGetTripExpenses } from "../../api/api.get";
import ConfirmDialog from "../../components/dialog/confirm.dialog";
import CreateTripExpenseDialog from "../../components/dialog/create-trip-expense.dialog";
import { useNotifications } from "../../components/notifications/notification";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { useAuthContext } from "../../providers/auth.provider";
import { useCurrencyContext } from "../../providers/currency.provider";
import { useGroupContext } from "../../providers/groups.provider";
import { IExpense2 } from "../../types/types";
import { toFixedDisplay } from "../../utils/number.utils";

export default function TripViewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { defaultGroupId } = useGroupContext();
  const { currency } = useCurrencyContext();
  const { user } = useAuthContext();
  const { notification, notifyError } = useNotifications();

  const { mutateAsync: deleteAsync } = useDeleteTripExpense(defaultGroupId, id);
  const { data: tripData, isPending: isTripDataLoading } = useGetTripById(
    defaultGroupId,
    id,
  );

  const {
    data: tripExpenses,
    isPending: isTripExpensesLoading,
    refetch: refetchTripExpenses,
  } = useGetTripExpenses(defaultGroupId, id);

  const tripDuration = useMemo(() => {
    if (tripData) {
      const days = dayjs(tripData.toDate).diff(dayjs(tripData.fromDate), "d");
      return days;
    }
    return 0;
  }, [tripData]);

  // Calculate expense breakdown by category
  const categoryBreakdown = {};

  const total = useMemo(() => {
    if (tripExpenses && tripExpenses.length > 0) {
      return tripExpenses.reduce((acc: number, curr: IExpense2) => {
        return acc + curr.amount;
      }, 0);
    }
    return 0;
  }, [tripExpenses]);

  const [deleteableExpenseId, setDeleteableExpenseId] = useState<
    string | undefined
  >();

  const columns = [
    {
      title: "Description",
      dataIndex: "comments",
      key: "name",
    },

    {
      title: "Amount",
      key: "amount",
      render: (item: IExpense2) => toFixedDisplay(item.amount),
      sorter: (a: IExpense2, b: IExpense2) => a.amount - b.amount,
      sortDirections: ["descend", "ascend"] as any[],
    },
    {
      title: "Date",
      dataIndex: "expenseDate",
      key: "expenseDate",
      render: (date: string) => (
        <span>{dayjs(date).format("DD MMM YYYY")}</span>
      ),
    },
    {
      title: "Method",
      dataIndex: "method",
      key: "method",
    },

    {
      title: "",
      width: 30,
      render: (_: any, record: IExpense2) => (
        <Button
          type="text"
          icon={<DeleteFilled />}
          onClick={() => setDeleteableExpenseId(record.id)}
          disabled={record.username !== user?.username}
        />
      ),
    },
  ];

  const handleDeleteExpense = () => {
    deleteAsync({ expenseId: deleteableExpenseId! })
      .then(() => refetchTripExpenses())
      .catch(() => notifyError("Whoops! Please try again."))
      .finally(() => setDeleteableExpenseId(undefined));
  };

  const [showAddExpenseDialog, setShowAddExpenseDialog] =
    useState<boolean>(false);

  return (
    <div className="min-h-screen bg-slate-50">
      <main className="pt-8 pb-12 px-4 sm:px-6 lg:px-8">
        {notification}
        {deleteableExpenseId && (
          <ConfirmDialog
            open={!!deleteableExpenseId}
            onCancel={() => setDeleteableExpenseId(undefined)}
            onOk={handleDeleteExpense}
            title="Are you sure you would like to delete this expense?"
          />
        )}
        {showAddExpenseDialog && (
          <CreateTripExpenseDialog
            onCancel={() => setShowAddExpenseDialog(false)}
            onSuccess={() => {
              setShowAddExpenseDialog(false);
              refetchTripExpenses();
            }}
            open={showAddExpenseDialog}
            tripId={id}
            groupId={defaultGroupId}
          />
        )}
        <div className="max-w-7xl mx-auto">
          {/* Back Button */}
          <Button type="link" onClick={() => navigate("/trips")}>
            Back
          </Button>

          {/* Two Panel Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Panel: Trip Details */}
            <div className="lg:col-span-1 space-y-6">
              {/* Trip Header Card */}
              <Card
                loading={isTripDataLoading}
                className="bg-gradient-to-br from-primary/10 to-indigo-100 border-primary/20"
              >
                <CardContent className="pt-6">
                  <div className="mb-4">
                    <h1 className="text-3xl font-bold text-foreground mb-2">
                      {tripData?.name}
                    </h1>
                    <div className="flex items-center gap-2 text-primary font-semibold">
                      {tripData?.destination}
                    </div>
                  </div>

                  <div className="space-y-3 pt-4 border-t border-primary/20">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <CalendarFilled className="w-4 h-4" />
                        <span className="text-sm">Duration</span>
                      </div>
                      <span className="text-lg font-semibold text-foreground">
                        {tripDuration} days
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <DollarCircleFilled className="w-4 h-4" />
                        <span className="text-sm">Total Spent</span>
                      </div>
                      <span className="text-2xl font-bold text-primary">
                        {currency}
                        {toFixedDisplay(total)}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Trip Dates Card */}
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CalendarFilled className="w-5 h-5 text-primary" />
                    Trip Dates
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <p className="text-xs uppercase text-muted-foreground mb-1">
                      From
                    </p>
                    <p className="font-semibold text-foreground">
                      {dayjs(tripData?.fromDate).format("DD MMM YYYY")}
                    </p>
                  </div>
                  <div className="my-3">
                    <p className="text-xs text-muted-foreground uppercase mb-1">
                      To
                    </p>
                    <p className="font-semibold text-foreground">
                      {dayjs(tripData?.toDate).format("DD MMM YYYY")}
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Category Breakdown Card */}
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    Spending by Category
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {Object.entries(categoryBreakdown).map(
                    ([category, amount]) => (
                      <div
                        key={category}
                        className="flex justify-between items-center py-2"
                      >
                        <span className="text-sm font-medium text-foreground">
                          {category}
                        </span>
                        <span className="text-sm font-semibold text-primary">
                          -
                        </span>
                      </div>
                    ),
                  )}
                  <div className="pt-3 border-t-2 border-slate-200 flex justify-between items-center font-semibold">
                    <span className="text-foreground">Total</span>
                    <span className="text-lg text-primary">
                      {currency}
                      {toFixedDisplay(total)}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Panel: Expenses List */}
            <div className="lg:col-span-2">
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarCircleFilled className="w-5 h-5 text-primary" />
                    Trip Expenses
                  </CardTitle>
                  <CardDescription>
                    <div className="flex justify-between">
                      {tripExpenses?.length} expenses recorded for this trip
                      <Button
                        type="link"
                        onClick={() => setShowAddExpenseDialog(true)}
                      >
                        Add Expense
                      </Button>
                    </div>
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table
                      dataSource={tripExpenses as any}
                      columns={columns}
                      loading={isTripExpensesLoading}
                      pagination={{ pageSize: 50 }}
                    />
                  </div>

                  {/* Summary Footer */}
                  <div className="mt-6 pt-4 border-t-2 border-slate-200 flex justify-between items-center">
                    <p className="font-semibold text-foreground">
                      Total Expenses
                    </p>
                    <p className="text-2xl font-bold text-primary">
                      {currency}
                      {toFixedDisplay(total)}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
